/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 원격 데이터 프로바이더 클래스를 정의한다.
 *
 * @file /src/models/store/provider/Remote.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 22.
 */
import * as parent from '@models/store/Provider';
import { Stores } from '@models/store/Store';
import { Ajax, Results } from '@utils/Ajax';
import { UUID } from '@utils/UUID';
import { Exceptions } from '@utils/Exceptions';
import { Format } from '@utils/Format';
import { Record } from '@models/store/Record';

export interface Properties extends parent.Properties {
    /**
     * 원격주소
     */
    url: string;

    /**
     * 데이터가 있는 필드명
     */
    recordsField?: string;

    /**
     * 데이터 총 갯수가 있는 필드명
     */
    totalField?: string;

    /**
     * 원격그룹여부
     */
    remoteGroup?: boolean;

    /**
     * 원격정렬여부
     */
    remoteSort?: boolean;

    /**
     * 원격필터여부
     */
    remoteFilter?: boolean;

    /**
     * 원격필드여부
     */
    remoteFields?: boolean;
}

export class Remote extends parent.Provider {
    /**
     * 원격로딩을 위한 매개변수 빌더
     */
    paramBuilder: () => Promise<{ [key: string]: any }> = null;

    /**
     * 데이터가 있는 필드명
     */
    recordsField: string;

    /**
     * 데이터 총 갯수가 있는 필드명
     */
    totalField: string;

    /**
     * 원격그룹여부
     */
    remoteGroup: boolean;

    /**
     * 원격정렬여부
     */
    remoteSort: boolean;

    /**
     * 원격필터여부
     */
    remoteFilter: boolean;

    /**
     * 원격필드여부
     */
    remoteFields: boolean;

    /**
     * 현재 데이터를 불러오고 있는 로더고유값
     */
    loaderId: string;

    /**
     * 현재 데이터를 가져온 URL
     */
    loadedUrl: string = null;

    /**
     * 현재 데이터를 가져온 URL 매개변수
     */
    loadedParams: { [key: string]: any } = null;

    /**
     * 데이터로더
     */
    loaders: Map<string, Promise<{ [key: string]: any }[]>> = new Map();

    /**
     * 데이터로더 결과데이터
     */
    loaderResults: Map<string, Results> = new Map();

    /**
     * 데이터 로딩시각
     */
    loadedAt: number = 0;

    /**
     * 원격 데이터 프로바이더 클래스를 정의한다.
     *
     * @param store - 데이터스토어 인스턴스
     * @param properties - 초기속성
     */
    constructor(
        public store: Stores,
        public properties: Properties = null
    ) {
        super(store, properties);

        this.url = this.properties?.url ?? null;
        this.paramBuilder = this.properties?.paramBuilder ?? null;

        this.recordsField = this.properties?.recordsField ?? 'records';
        this.totalField = this.properties?.totalField ?? 'total';

        this.remoteGroup = this.properties?.remoteGroup === true;
        this.remoteSort = this.properties?.remoteSort === true;
        this.remoteFilter = this.properties?.remoteFilter === true;
        this.remoteFields = this.properties?.remoteFields === true;
    }

    /**
     * 데이터를 불러오기 위한 매개변수를 가져온다.
     *
     * @returns 매개변수
     */
    async getLoaderParams(): Promise<{ [key: string]: any }> {
        let params = this.getParams();

        if (this.paramBuilder !== null) {
            params = { ...params, ...(await this.paramBuilder()) };
        }

        if (this.remoteFields == true) {
            const fields = Object.keys(this.store.fields);
            if (fields.length > 0) {
                params.fields = fields.join(',');
            }
        }

        if (this.limit > 0) {
            if (this.page instanceof Record) {
                params.start = this.page.getPrimaryData();
            } else if (typeof this.page == 'object') {
                params.start = JSON.stringify(this.page);
            } else {
                params.start = (this.page - 1) * this.limit;
            }
            params.limit = this.limit;
        }

        let sorters = null;
        if (this.remoteGroup == true) {
            if (this.store.grouper == null) {
                sorters = null;
            } else {
                sorters = this.store.grouper.sorters;
            }
        }

        if (this.remoteSort == true) {
            if (this.store.sorters === null) {
                sorters = null;
            } else {
                sorters = sorters !== null ? Object.assign(sorters, this.store.sorters) : this.store.sorters;
            }
        }

        if (sorters !== null) {
            params.sorters = JSON.stringify(sorters);
        }

        if (this.remoteFilter == true) {
            if (this.store.filters === null) {
                params.filters = null;
            } else {
                params.filters = JSON.stringify(this.store.filters);
                params.filterMode = this.store.filterMode;
            }
        }

        return params;
    }

    /**
     * 데이터를 불러온다.
     */
    async load(): Promise<void> {
        if (this.isLoadable() == false) {
            return;
        }

        return this.#load();
    }

    /**
     * 데이터 로더를 초기화한다.
     */
    async reset(): Promise<void> {
        this.loaded = false;
        if (this.loaderId !== null && this.loaders.has(this.loaderId) == true) {
            await this.loaders.get(this.loaderId);
        }

        this.loaders.clear();
        this.loaderResults.clear();
    }

    /**
     * 변경된 데이터를 커밋한다.
     *
     * @param is_all - 전체 데이터를 커밋할지 여부 (false 인 경우 변경된 데이터만 커밋)
     * @returns 변경 완료 여부
     */
    async commit(is_all: boolean = false): Promise<boolean> {
        const records = [];

        if (is_all == true) {
            for (const record of this.store.getRecords().values() ?? []) {
                records.push({ origin: record.origin, updated: record.getData() });
            }
        } else {
            for (const record of this.store.updatedRecords.values() ?? []) {
                records.push({ origin: record.origin, updated: record.getUpdated() });
            }
        }

        const results = await Ajax.patch(this.url, { records }, (await this.getLoaderParams()) ?? null);
        if (results.success == true) {
            for (const record of this.store.getRecords().values()) {
                if (record.isUpdated() == true) {
                    record.commit();
                }
            }

            return true;
        }

        return false;
    }

    /**
     * 데이터스토어를 다시 불러올 수 있는 상태인지 확인한다.
     *
     * @returns 리로드가능여부
     */
    isLoadable(): boolean {
        return (
            this.loaded === false ||
            this.loadedUrl != this.url ||
            Format.isEqual(this.getParams(), this.loadedParams) == false
        );
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.loaders.clear();
        this.loaderResults.clear();
        this.paramBuilder = null;
        super.destroy();
    }

    /**
     * 실제 데이터를 불러온다.
     */
    async #load(): Promise<void> {
        const records: { [key: string]: any }[] = [];

        this.loadedUrl = this.url;
        this.loadedParams = this.getParams();

        if (this.loader !== null) {
            await this.store.onBeforeLoad();
            records.push(...(await this.loader()));
        } else {
            const params = await this.getLoaderParams();
            this.loaderId = UUID.v5(UUID.URL_NAMESPACE, JSON.stringify({ url: this.url, params }));

            if (this.loaders.has(this.loaderId) == false) {
                this.loaders.set(
                    this.loaderId,
                    (async () => {
                        try {
                            await this.store.onBeforeLoad();
                            const results = this.loaderResults.get(this.loaderId) ?? (await Ajax.get(this.url, params));
                            this.loaderResults.set(this.loaderId, { ...results });

                            if (results.success == true) {
                                const start = results.start ?? null;
                                const limit = results.limit ?? this.limit;
                                if (start !== null && limit > 0) {
                                    this.page = Math.floor(start / limit) + 1;
                                }

                                if (
                                    results[this.recordsField] !== undefined &&
                                    Array.isArray(results[this.recordsField]) == true
                                ) {
                                    this.total = results[this.totalField];
                                    this.updatedAt = results['updated_at'] ?? Math.round(new Date().getTime() / 1000);

                                    return results[this.recordsField];
                                }

                                return [];
                            }
                        } catch (e) {
                            Exceptions.print(Exceptions.base(e));

                            this.loaderResults.set(this.loaderId, { success: false });

                            this.total = 0;
                            this.updatedAt = 0;
                            return [];
                        }
                    })()
                );
            }

            const results = await this.loaders.get(this.loaderId);
            records.push(...results);
        }

        await this.store.init(records);

        this.loaderId = null;
        this.loaded = true;
    }
}
